import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react'
import { api } from './api.js'
import { pushToken } from './native.js'

const SessionContext = createContext(null)

async function loadSession() {
  try {
    const [me, meta] = await Promise.all([api('/api/me'), api('/api/meta')])
    return { user: me.user, meta }
  } catch {
    return { user: null, meta: null }
  }
}

// Loads the signed-in user and site settings once, for the whole app.
export function SessionProvider({ children }) {
  const [session, setSession] = useState({ user: undefined, meta: null })

  useEffect(() => {
    let active = true
    const run = async () => {
      const next = await loadSession()
      if (active) setSession(next)
    }
    run()
    return () => {
      active = false
    }
  }, [])

  const refresh = useCallback(async () => {
    setSession(await loadSession())
  }, [])

  // Keep the notification badge current while the tab is open.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refresh()
    }, 60_000)
    return () => clearInterval(timer)
  }, [refresh])

  const signOut = useCallback(async () => {
    // In the iOS app, this phone stops getting the account's pushes.
    const token = pushToken()
    await api('/auth/logout', { method: 'POST', body: token ? { pushToken: token } : undefined })
    setSession((s) => ({ ...s, user: null }))
  }, [])

  const value = useMemo(
    () => ({ user: session.user, meta: session.meta, loading: session.user === undefined, refresh, signOut }),
    [session, refresh, signOut],
  )
  return <SessionContext value={value}>{children}</SessionContext>
}

export function useSession() {
  const value = use(SessionContext)
  if (!value) throw new Error('useSession must be used inside <SessionProvider>')
  return value
}
